<template>
  <section class="chart-card">
    <h3 class="card-title">
      {{ t('workbench.dataStats.businessStats') }}
    </h3>
    <div
      ref="chartRef"
      class="chart-box"
      :class="`is-${chartMode}`"
    />
  </section>
</template>

<script setup>
import { computed, markRaw, nextTick, onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import { getBusinessHandlingStatistics } from '@/api/normalChannelStatistics'

const { t, locale } = useI18n()
const SERIES_ORIGINAL = computed(() => t('workbench.dataStats.originalDigitalFiling'))
const SERIES_PRODUCT = computed(() => t('workbench.dataStats.productDeposit'))
const DEFAULT_ITEMS = computed(() => [
  { name: SERIES_ORIGINAL.value, value: 0, percentage: '0.00%', currentMonthTotal: 0 },
  { name: SERIES_PRODUCT.value, value: 0, percentage: '0.00%', currentMonthTotal: 0 }
])

const chartRef = useTemplateRef('chartRef')
const chartMode = shallowRef('desktop')
let chart = null
let resizeObserver = null
let resizeFrame = 0
let itemsCache = []
let totalCache = 0

function resolveChartMode() {
  const width = chartRef.value?.clientWidth || window.innerWidth || 0
  if (width <= 520 || window.innerWidth <= 768) return 'mobile'
  if (width <= 900 || window.innerWidth <= 1024) return 'compact'
  return 'desktop'
}

function buildPieLayout(mode) {
  if (mode === 'mobile') {
    return {
      center: ['50%', '38%'],
      radius: ['28%', '46%'],
      legend: {
        orient: 'vertical',
        left: 'center',
        bottom: 10,
        itemGap: 16,
        textStyle: {
          rich: {
            name: { width: 108, color: '#333', fontSize: 13, lineHeight: 20 },
            value: { width: 36, color: '#333', fontSize: 13, fontWeight: 'bold', align: 'right', lineHeight: 20 },
            percent: { width: 62, padding: [0, 0, 0, 8], color: '#999', fontSize: 13, align: 'right', lineHeight: 20 },
            monthly: {
              width: 206,
              padding: [4, 0, 0, 0],
              color: '#999',
              fontSize: 12,
              lineHeight: 18,
              align: 'left'
            }
          }
        }
      },
      graphicLeft: '50%',
      graphicTop: '38%',
      valueFontSize: 26,
      valueY: -10,
      nameY: 16
    }
  }
  return {
    center: ['50%', '52%'],
    radius: mode === 'compact' ? [52, 82] : [58, 92],
    legend: {},
    graphicLeft: '50%',
    graphicTop: '52%',
    valueFontSize: mode === 'compact' ? 22 : 24,
    valueY: -12,
    nameY: mode === 'compact' ? 16 : 18
  }
}

function buildCenterGraphic(total, layout) {
  return [{
    id: 'businessPieCenter',
    type: 'group',
    left: layout.graphicLeft,
    top: layout.graphicTop,
    bounding: 'raw',
    children: [
      {
        type: 'text',
        y: layout.valueY,
        style: {
          text: String(total),
          fill: '#333',
          fontSize: layout.valueFontSize,
          fontWeight: 'bold',
          textAlign: 'center',
          textVerticalAlign: 'middle'
        }
      },
      {
        type: 'text',
        y: layout.nameY,
        style: {
          text: t('workbench.dataStats.businessTotal'),
          fill: '#999',
          fontSize: 12,
          textAlign: 'center',
          textVerticalAlign: 'middle'
        }
      }
    ]
  }]
}

function buildDetailGraphics(list, layout) {
  if (!chart || chartMode.value === 'mobile') return []
  const width = chart.getWidth()
  const height = chart.getHeight()
  const centerX = width * 0.5
  const centerY = height * 0.52
  const outerRadius = Array.isArray(layout.radius)
    ? Number(layout.radius[1])
    : Math.min(width, height) * 0.31
  const defaults = DEFAULT_ITEMS.value
  const original = list.find(item => item.name === SERIES_ORIGINAL.value) || defaults[0]
  const product = list.find(item => item.name === SERIES_PRODUCT.value) || defaults[1]
  const leftY = centerY - outerRadius * 0.6
  const rightY = centerY + outerRadius * 0.32
  const leftLineEndX = width * 0.34
  const rightLineEndX = width * 0.68
  const buildTextGroup = (id, left, top, title, currentMonthTotal) => ({
    id,
    type: 'group',
    left,
    top,
    bounding: 'raw',
    children: [
      {
        type: 'text',
        style: {
          text: title,
          fill: '#333',
          font: '500 13px "Microsoft YaHei", sans-serif',
          textVerticalAlign: 'middle'
        }
      },
      {
        type: 'text',
        y: 23,
        style: {
          text: t('workbench.dataStats.monthlyNewDetail', { n: currentMonthTotal || 0 }),
          fill: '#999',
          font: '12px "Microsoft YaHei", sans-serif',
          textVerticalAlign: 'middle'
        }
      }
    ]
  })
  return [
    {
      id: 'businessLeftLine',
      type: 'polyline',
      shape: {
        points: [
          [centerX - outerRadius * 0.72, centerY - outerRadius * 0.7],
          [centerX - outerRadius - 24, leftY],
          [leftLineEndX, leftY]
        ]
      },
      style: { stroke: '#B45A30', lineWidth: 1.5, fill: null }
    },
    {
      id: 'businessLeftDot',
      type: 'circle',
      shape: { cx: leftLineEndX, cy: leftY, r: 4 },
      style: { fill: '#B45A30' }
    },
    buildTextGroup(
      'businessLeftText',
      '13%',
      leftY - 11,
      t('workbench.dataStats.handleProductTotal', { n: product.value || 0 }),
      product.currentMonthTotal
    ),
    {
      id: 'businessRightLine',
      type: 'polyline',
      shape: {
        points: [
          [centerX + outerRadius * 0.72, centerY + outerRadius * 0.7],
          [centerX + outerRadius + 24, rightY],
          [rightLineEndX, rightY]
        ]
      },
      style: { stroke: '#FFB21D', lineWidth: 1.5, fill: null }
    },
    {
      id: 'businessRightDot',
      type: 'circle',
      shape: { cx: rightLineEndX, cy: rightY, r: 4 },
      style: { fill: '#FFB21D' }
    },
    buildTextGroup(
      'businessRightText',
      '70%',
      rightY - 11,
      t('workbench.dataStats.handleOriginalTotal', { n: original.value || 0 }),
      original.currentMonthTotal
    )
  ]
}

function renderChart() {
  if (!chart) return
  chartMode.value = resolveChartMode()
  const mode = chartMode.value
  const layout = buildPieLayout(mode)
  const defaults = DEFAULT_ITEMS.value
  const list = itemsCache.length ? itemsCache : defaults
  const colorMap = {
    [SERIES_ORIGINAL.value]: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
      { offset: 0, color: '#FFB91F' },
      { offset: 1, color: '#FFE39B' }
    ]),
    [SERIES_PRODUCT.value]: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
      { offset: 0, color: '#D9B19D' },
      { offset: 1, color: '#A74E24' }
    ])
  }
  const graphics = buildCenterGraphic(totalCache, layout)
  graphics.push(...buildDetailGraphics(list, layout))
  chart.setOption({
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: '{b}<br/>{c}（{d}%）'
    },
    graphic: graphics,
    legend: {
      ...layout.legend,
      show: mode === 'mobile',
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      data: list.map(item => item.name),
      formatter: (name) => {
        const item = list.find(current => current.name === name) || defaults[0]
        return [
          `{name|${name}}{value|${item.value}}{percent|${item.percentage}}`,
          `{monthly|${t('workbench.dataStats.monthlyNewLegend', { n: item.currentMonthTotal || 0 })}}`
        ].join('\n')
      }
    },
    series: [{
      name: t('workbench.dataStats.businessStats'),
      type: 'pie',
      center: layout.center,
      radius: layout.radius,
      startAngle: 90,
      clockwise: true,
      minAngle: 0,
      stillShowZeroSum: false,
      label: { show: false },
      labelLine: { show: false },
      data: list.map(item => ({
        name: item.name,
        value: item.value,
        itemStyle: { color: colorMap[item.name] }
      }))
    }]
  }, { replaceMerge: ['graphic', 'legend', 'series'] })
}

function resolveSeriesName(rawName) {
  const name = String(rawName || '').trim()
  const originalAliases = ['原创数字备案', '原创作品备案', 'original', 'originalTotal', SERIES_ORIGINAL.value]
  const productAliases = ['产品发布存证', 'product', 'productTotal', SERIES_PRODUCT.value]
  if (originalAliases.includes(name)) return SERIES_ORIGINAL.value
  if (productAliases.includes(name)) return SERIES_PRODUCT.value
  return name || '-'
}

async function loadStatistics() {
  try {
    const response = await getBusinessHandlingStatistics()
    const payload = response && response.code === 200 ? (response.data || {}) : {}
    totalCache = Number(payload.total) || 0
    const sourceItems = Array.isArray(payload.items) ? payload.items : []
    const mapped = sourceItems.map(item => ({
      name: resolveSeriesName(item.name),
      value: Number(item.value) || 0,
      percentage: item.percentage || '0.00%',
      currentMonthTotal: Number(item.currentMonthTotal) || 0
    }))
    // 保证两系列齐全，便于图例/标注稳定展示
    itemsCache = DEFAULT_ITEMS.value.map((defaults) => {
      const hit = mapped.find(item => item.name === defaults.name)
      return hit || { ...defaults }
    })
  } catch (error) {
    console.error('查询业务办理统计失败:', error)
    totalCache = 0
    itemsCache = DEFAULT_ITEMS.value
  }
  renderChart()
}

watch(locale, () => {
  loadStatistics()
})

onMounted(async () => {
  await nextTick()
  if (!chartRef.value) return
  chart = markRaw(echarts.init(chartRef.value))
  renderChart()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0
        chart?.resize()
        renderChart()
      })
    })
    resizeObserver.observe(chartRef.value)
  }
  loadStatistics()
})

onBeforeUnmount(() => {
  if (resizeFrame) cancelAnimationFrame(resizeFrame)
  resizeObserver?.disconnect()
  chart?.dispose()
})
</script>

<style lang="scss" scoped>
.chart-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 20px 24px 16px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-title {
  margin: 0 0 8px;
  color: #333;
  font-size: 18px;
  font-weight: bold;
}

.chart-box {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 260px;

  &.is-compact {
    height: 280px;
  }

  &.is-mobile {
    height: 390px;
  }
}

@media (max-width: 768px) {
  .chart-card {
    padding: 16px;
  }

  .card-title {
    margin-bottom: 16px;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .chart-card {
    padding: 12px;
  }

  .card-title {
    margin-bottom: 12px;
    font-size: 15px;
  }

  .chart-box.is-mobile {
    height: 380px;
  }
}
</style>
